import { useRef,  useState , memo , useCallback} from 'react';
import Map from '../components/map/Map.js'
import Grid from '@mui/material/Grid'
import {useFetchFacility} from '../hooks/useFetchFacility'
import {useFetchToilet} from '../hooks/useFetchToilet'
import {useFetchRouting} from '../hooks/useFetchRouting';
import {Marker, Tooltip, Polyline} from 'react-leaflet'
import Header from "../components/Header";
import {ToiletIcon , RestaurantIcon} from '../components/map/MapIcon.js'
import MapCardStation from '../components/map/MapCardStation.js'
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {useSharedLang} from '../hooks/useSharedLang.js';
import {LANGS , getInsta360Url , geoPositionOptions} from "../components/constants.js";
import {useGeolocated} from '../hooks/useGeolocated';
import {useDikstra} from '../hooks/useDikstra';
import Loading from "../components/Loading.js";
import {isRestaurant, isToilet} from "../components/constants.js";
import {useNavigate} from "react-router-dom";
import {getLink , getIcon , getTooltipOptions} from "../utils/UrlUtil.js";

/**
 * 2025/06/29 駅/食事を施設と乗り物に分離
 */
const Facility = memo(() => {
    const mapRef = useRef(null);

    const {facilityList, isLoading} = useFetchFacility();
    const {toiletList, isToiletLoading} = useFetchToilet();
    const {routingList, isRouteLoading} = useFetchRouting();
    const [inRouteGuid, setInRouteGuid] = useState(false);
    const selectedMarkerKey = useRef(null);
    
    const defaultSelected = useRef(null);
    const isUseDefaultSelectedFacility = useRef(true);
    const activeFacilityList = useRef(null);

    const { id } = useParams();
    const { t } = useTranslation();
    const { sharedLang } = useSharedLang();
    const navigate = useNavigate();

    /**
     * リアルタイム座標チェック
     * 
     * @param {*} pos 
     */
    const setCurrentPosition = (pos) => {
        //現在地をマップに表示
        if(mapRef.current != null){
            mapRef.current.updateMarker(pos.coords.latitude, pos.coords.longitude);
            lastCoords.current =  pos;
        }
    };

    //--- 現在地 ---
    //useGeolocated の coordsは使わない。使うと座標が更新されるたびに画面が再描写してしまう。
    const { isGeolocationAvailable, isGeolocationEnabled  } = useGeolocated({
        ...geoPositionOptions , 
        onSuccess : (pos) => setCurrentPosition(pos),
    });
    const lastCoords = useRef(null);

    
    const { routing, getNearNode } = useDikstra({routingData : routingList})

    const [selectedFacility , setSelectedFacility] = useState(null);

    const getMarkerKey = useCallback((facility , coordinate) => {
        return ( '00000' + facility.id).slice( -5 ) + '-' + coordinate[0] + '-' + coordinate[1];
    },[]);

    const parseMarkerKey = useCallback((markerKey) => {
        const [id, latitude, longitude] = markerKey.split('-');
        return {id:id , longitude:longitude, latitude:latitude}
    },[]);

    const toggleRouteGuid = (flg) => {
        setInRouteGuid(flg);
    };

    const clickMarker = (facility, coordinate) => {
        selectedMarkerKey.current = getMarkerKey(facility, coordinate);
        setSelectedFacility(facility);
    };

    const resetMarker = useCallback(() => {
        selectedMarkerKey.current = null;
        isUseDefaultSelectedFacility.current = false;
        setSelectedFacility(null);
        setInRouteGuid(false);
    },[]);
    
    const selectStation = useCallback((facility) => {
        selectedMarkerKey.current = null;
        setSelectedFacility(facility);
    },[]);

    const isActiveMarker = (facility) => {
        let currentElement = getActiveElement();   

        return (!currentElement && isRestaurant(facility)) || 
            (currentElement && currentElement.id === facility.id);
    };

    const modalOpen = useCallback(() => {
        // setIsOpen(true);
        navigate(
            '/modal360', 
            { state: { link360: getInsta360Url(selectedFacility) ,returnUrl: getLink(selectedFacility)}
        });

    },[selectedFacility]);

    const getActiveElement = useCallback(() => {
        let ret = selectedFacility;
        if(selectedFacility == null && isUseDefaultSelectedFacility.current){
            ret = defaultSelected.current;
        }
        return ret;
    },[selectedFacility]);

    const getPolyline = useCallback(() => {
        let selectedMarker = parseMarkerKey(selectedMarkerKey.current);
        if(lastCoords.current == null || selectedMarker == null){
            return [];
        }

        let routList =  routing(
            getNearNode(lastCoords.current.coords),
            getNearNode({
                latitude: selectedMarker.latitude,
                longitude: selectedMarker.longitude
            })
        );
        let ret = [];
        for (let i = 0; i < routList.length; i++) {
            ret[i] = [routList[i].latitude, routList[i].longitude];
        }
        return ret;
    },[selectedFacility, lastCoords.current]);

    const getActiveFacility = () => {
        let elements = [];
        if(isLoading){
          return elements;
        }
        let allList = toiletList.concat(facilityList);

        allList.forEach(row => {
            if(sharedLang === LANGS.ENGLISH){
                row.name = row.nameEnglish;
                row.description = row.descriptionEnglish;
                if(row.category === 'restaurant'){
                    row.lineName = row.lineNameEnglish;
                    row.open = row.openEnglish;
                    row.shopInfo = row.shopInfoEnglish;
                }
                else if(row.category === 'toilet'){
                    row.catchcopy = row.catchcopyEnglish;
                }
            }
            elements.push(row);
        })
        return elements;
    }

    if (isLoading || isToiletLoading || isRouteLoading) {
        return (<Loading></Loading>);
    }
    activeFacilityList.current = getActiveFacility();

    if(id && defaultSelected.current == null){
        activeFacilityList.current.forEach((element) =>{
            if(String(element.id) === id){
                defaultSelected.current = element;
                selectStation(element);
                selectedMarkerKey.current = getMarkerKey(
                    element,
                    [element.positions[0].latitude , element.positions[0].longitude]
                );
            }
        })
    }

    return (
        <>
        <Header 
          display={true} 
          title={t("facility.title")} 
          displayList={false}
        />
        <Grid container justifyContent="center" alignItems="center" direction="column">
        <Map ref={mapRef}>
        {
            activeFacilityList.current.map((element, fIndex) =>
                element.positions.map((position, pIndex) => {
                    let coordinate = [position.latitude, position.longitude];
                    let markerKey = getMarkerKey(element, coordinate);

                    return <Marker 
                        key={markerKey} 
                        position={coordinate}
                        zIndexOffset={isToilet(element)?100:200}
                        icon={getIcon(element.markerIcon)}
                        eventHandlers={{
                            click: (e) => {
                              clickMarker(element, coordinate);
                            }
                          }}
                        >
                        {
                        isActiveMarker(element) &&
                        <Tooltip
                            {...getTooltipOptions(
                                fIndex + pIndex , 
                                coordinate,
                                element.category
                            )}
                        >
                            {element.name}
                        </Tooltip>
                    }
                    </Marker>
                })
            )
        }
         <div>{inRouteGuid && <Polyline positions={getPolyline()} />}</div>
        </Map>
        <MapCardStation 
            data={getActiveElement()} 
            resetMarker={resetMarker} 
            isRouteEnabled={isGeolocationAvailable && isGeolocationEnabled && lastCoords.current != null}
            inRouteGuid = {inRouteGuid}
            toggleRouteGuid = {toggleRouteGuid}
            modalOpen = {modalOpen}        
            />     
        </Grid>
        </>
    )
});
export default Facility